import React, { useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import AppLayout from "@amzn/awsui-components-react/polaris/app-layout";
import {
  ContentLayout,
  Header,
  Select,
  SelectProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import { DashboardType, QuickSightDashboard } from "./QuicksightDashboard";
import PageHeader from "../PageHeader";
import DarkModeContext from "../../contexes/DarkModeContext";
import { StageContext } from "../../contexes/OrderIdContext";
import { Stage } from "../../lusca-search-api/generated-src";
import { DARK_THEME } from "../../constants";

// Maps the `/dashboard/oms/:product` path segment to its backend dashboard type.
const PRODUCTS: { value: string; label: string; dashboardType: DashboardType }[] = [
  { value: "channels", label: "PV Channels", dashboardType: "pv_channels" },
  { value: "addon", label: "PV Addon", dashboardType: "pv_addon" },
  { value: "tvod", label: "PV TVOD", dashboardType: "pv_tvod" },
];

const DEFAULT_PRODUCT = PRODUCTS[0];
const BASE_PATH = "/dashboard/oms";

const OMSDashboard: React.FC = () => {
  const { product: productParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(
    () => window.localStorage.getItem("theme") === DARK_THEME
  );
  const [, setShareHandler] = useState<(() => Promise<string>) | null>(null);

  // Redirect legacy `?product=` links to the canonical `/dashboard/oms/:product`
  // path, preserving any other query params (e.g. filters).
  const legacyProduct = searchParams.get("product");
  if (!productParam && legacyProduct) {
    const params = new URLSearchParams(searchParams);
    params.delete("product");
    const search = params.toString();
    return (
      <Navigate
        to={`${BASE_PATH}/${legacyProduct}${search ? `?${search}` : ""}`}
        replace
      />
    );
  }

  // An unrecognized product segment falls back to the default page.
  if (productParam && !PRODUCTS.some((p) => p.value === productParam)) {
    return <Navigate to={BASE_PATH} replace />;
  }

  const product =
    PRODUCTS.find((p) => p.value === productParam) || DEFAULT_PRODUCT;

  const handleProductChange = (value: string) => {
    // Filters are dashboard-specific, so drop them when switching products.
    const params = new URLSearchParams(searchParams);
    params.delete("filters");
    const search = params.toString();
    navigate(`${BASE_PATH}/${value}${search ? `?${search}` : ""}`);
  };

  const selectOptions: SelectProps.Option[] = PRODUCTS.map((p) => ({
    value: p.value,
    label: p.label,
  }));

  return (
    <DarkModeContext.Provider value={darkMode}>
      <StageContext.Provider value={Stage.Prod}>
        <PageHeader setDarkMode={setDarkMode} />
        <AppLayout
          content={
            <ContentLayout
              defaultPadding
              header={
                <Header
                  variant="h1"
                  actions={
                    <Select
                      selectedOption={{
                        value: product.value,
                        label: product.label,
                      }}
                      onChange={({ detail }) =>
                        handleProductChange(detail.selectedOption.value as string)
                      }
                      options={selectOptions}
                      ariaLabel="Select PV OMS dashboard"
                    />
                  }
                >
                  PV OMS Dashboard
                </Header>
              }
            >
              <SpaceBetween size="xl">
                <QuickSightDashboard
                  stage={Stage.Prod}
                  dashboardType={product.dashboardType}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  setShareHandler={setShareHandler}
                />
              </SpaceBetween>
            </ContentLayout>
          }
          contentType="default"
          headerSelector="#h"
          maxContentWidth={1700}
          navigationHide
          toolsHide
        />
      </StageContext.Provider>
    </DarkModeContext.Provider>
  );
};

export default OMSDashboard;
